import { concat as observableConcat, empty as observableEmpty, from as observableFrom, of as observableOf, Subject, asyncScheduler, } from "rxjs";
import { catchError, expand, finalize, first, map, mergeMap, last, publishReplay, refCount, startWith, tap, observeOn, } from "rxjs/operators";
import { GraphMode } from "./GraphMode";
import { SubscriptionHolder } from "../utils/SubscriptionHolder";
/**
 * @class GraphService
 *
 * @classdesc Represents a service for graph operations.
 */
export class GraphService {
    /**
     * Create a new graph service instance.
     *
     * @param {Graph} graph - Graph instance to be operated on.
     */
    constructor(graph) {
        this._dataAdded$ = new Subject();
        this._subscriptions = new SubscriptionHolder();
        this._onDataAdded = (event) => {
            this._graph$
                .pipe(first(), mergeMap((graph) => {
                return graph.updateCells$(event.cellIds).pipe(tap(() => {
                    graph.resetSpatialEdges();
                }));
            }))
                .subscribe((cellId) => {
                this._dataAdded$.next(cellId);
            });
        };
        const subs = this._subscriptions;
        // NOTE: Review in detail. What is going on here.
        this._graph$ = observableConcat(observableOf(graph), graph.changed$).pipe(publishReplay(1), refCount());
        subs.push(this._graph$.subscribe(() => {
            /*noop*/
        }));
        this._graphMode = GraphMode.Spatial;
        this._graphModeSubject$ = new Subject();
        this._graphMode$ = this._graphModeSubject$.pipe(startWith(this._graphMode), publishReplay(1), refCount());
        subs.push(this._graphMode$.subscribe(() => {
            /*noop*/
        }));
        this._firstGraphSubjects$ = [];
        this._initializeCacheSubscriptions = [];
        this._sequenceSubscriptions = [];
        this._spatialSubscriptions = [];
        graph.api.data.on("dataadded", this._onDataAdded);
    }
    get graph$() {
        return this._graph$.pipe(first());
    }
    /**
     * Get dataAdded$.
     *
     * @returns {Observable<string>} Observable emitting
     * a cell id every time data has been added to a cell.
     */
    get dataAdded$() {
        return this._dataAdded$;
    }
    /**
     * Get filter observable.
     *
     * @desciption Emits the filter every time it has changed.
     *
     * @returns {Observable<FilterFunction>} Observable
     * emitting the filter function every time it is set.
     */
    get filter$() {
        return this._graph$.pipe(first(), mergeMap((graph) => {
            return graph.filter$;
        }));
    }
    /**
     * Get graph mode observable.
     *
     * @description Emits the current graph mode.
     *
     * @returns {Observable<GraphMode>} Observable
     * emitting the current graph mode when it changes.
     */
    get graphMode$() {
        return this._graphMode$;
    }
    /**
     * Cache full nodes in a bounding box.
     *
     * @description When called, the full properties of
     * the node are retrieved. The node cache is not initialized
     * for any new nodes retrieved and the node assets are not
     * retrieved, {@link cacheBoundingBox$} needs to be called for caching
     * assets.
     *
     * @param {ILatLon} sw - South west corner of bounding box.
     * @param {ILatLon} ne - North east corner of bounding box.
     * @return {Observable<Array<Node>>} Observable emitting a single item,
     * the nodes of the bounding box, when they have all been retrieved.
     * @throws {Error} Propagates any IO node caching errors to the caller.
     */
    cacheBoundingBox$(sw, ne) {
        return this._graph$.pipe(first(), mergeMap((graph) => {
            return graph.cacheBoundingBox$(sw, ne);
        }));
    }
    /**
     * Cache full nodes in a cell.
     *
     * @description When called, the full properties of
     * the node are retrieved. The node cache is not initialized
     * for any new nodes retrieved and the node assets are not
     * retrieved, {@link cacheNode$} needs to be called for caching
     * assets.
     *
     * @param {string} cellId - Id of the cell.
     * @return {Observable<Array<Node>>} Observable emitting a single item,
     * the nodes of the cell, when they have all been retrieved.
     * @throws {Error} Propagates any IO node caching errors to the caller.
     */
    cacheCell$(cellId) {
        return this._graph$.pipe(first(), mergeMap((graph) => {
            return graph.cacheCell$(cellId);
        }));
    }
    /**
     * Cache a node in the graph and retrieve it.
     *
     * @description When called, the full properties of
     * the node are retrieved and the node cache is initialized.
     * After that the node assets are cached and the node
     * is emitted to the observable when.
     * In parallel to caching the node assets, the sequence and
     * spatial edges of the node are cached. For this, the sequence
     * of the node and the required tiles and spatial nodes are
     * retrieved. The sequence and spatial edges may be set before
     * or after the node is returned.
     *
     * @param {string} key - Key of the node to cache.
     * @return {Observable<Node>} Observable emitting a single item,
     * the node, when it has been retrieved and its assets are cached.
     * @throws {Error} Propagates any IO node caching errors to the caller.
     */
    // NOTE: (3) get call with other keys from cache component
    // FIXME: 70849-4
    cacheNode$(key) {
        // console.log("cacheNode$", key);
        // debugger;
        const firstGraphSubject$ = new Subject();
        this._firstGraphSubjects$.push(firstGraphSubject$);
        // NOTE: stream of graph initiallized with node == key in it. [graph(key), graph(key), .....]
        const firstGraph$ = firstGraphSubject$.pipe(publishReplay(1), refCount());
        // NOTE: actaull node for return value below.
        const node$ = firstGraph$.pipe(map((graph) => {
            return graph.getNode(key);
        }), publishReplay(1), refCount());
        node$.subscribe(undefined, (error) => {
            console.error(`Failed to cache node (${key})`, error);
        });
        // NOTE: Cache first node in graph is never fired again given checks in mergeMap.
        this._initializeCacheSubscription = this._graph$
            .pipe(first(), mergeMap((graph) => {
            if (graph.isCachingFull(key) || !graph.hasNode(key)) {
                // NOTE: (3)
                return graph.cacheFull$(key);
            }
            if (graph.isCachingFill(key) || !graph.getNode(key).full) {
                return graph.cacheFill$(key);
            }
            return observableOf(graph).pipe(observeOn(asyncScheduler));
        }), tap((graph) => {
            if (!graph.hasInitializedCache(key)) {
                graph.initializeCache(key);
            }
        }), finalize(() => {
            if (this._initializeCacheSubscription == null) {
                return;
            }
            this._removeFromArray(this._initializeCacheSubscription, this._initializeCacheSubscriptions);
            this._removeFromArray(firstGraphSubject$, this._firstGraphSubjects$);
        }))
            .subscribe((graph) => {
            firstGraphSubject$.next(graph);
            firstGraphSubject$.complete();
        }, (error) => {
            firstGraphSubject$.error(error);
        });
        if (!this._initializeCacheSubscription.closed) {
            this._initializeCacheSubscriptions.push(this._initializeCacheSubscription);
        }
        // NOTE: Stream of graph with _sequences set for key.
        const graphSequence$ = firstGraph$.pipe(mergeMap((graph) => {
            if (graph.isCachingNodeSequence(key) || !graph.hasNodeSequence(key)) {
                return graph.cacheNodeSequence$(key);
            }
            return observableOf(graph);
        }), publishReplay(1), refCount());
        const sequenceSubscription = graphSequence$
            .pipe(tap((graph) => {
            if (!graph.getNode(key).sequenceEdges.cached) {
                // NOTE: (3)
                graph.cacheSequenceEdges(key);
            }
        })
        // Not used at the moment, throwed a bug when playing
        // finalize((): void => {
        //     if (sequenceSubscription == null) {
        //         return;
        //     }
        //     this._removeFromArray(sequenceSubscription, this._sequenceSubscriptions);
        // })
        )
            .subscribe(() => {
            return;
        }, (error) => {
            console.error(`Failed to cache sequence edges (${key}).`, error);
        });
        // Not used at the moment, removed to not overflow an array as the removing throws a bug (see above)
        // if (!sequenceSubscription.closed) {
        //     this._sequenceSubscriptions.push(sequenceSubscription);
        // }
        if (this._graphMode === GraphMode.Spatial) {
            const spatialSubscription = firstGraph$
                .pipe(expand((graph) => {
                if (graph.hasTiles(key)) {
                    return observableEmpty();
                }
                return observableFrom(graph.cacheTiles$(key)).pipe(mergeMap((graph$) => {
                    return graph$.pipe(mergeMap((g) => {
                        // debugger;
                        if (g.isCachingTiles(key)) {
                            return observableEmpty();
                        }
                        // debugger;
                        return observableOf(g);
                    }), catchError((error) => {
                        console.warn(`Failed to cache tile data (${key}).`, error);
                        return observableEmpty();
                    }));
                }));
            }), last(), mergeMap((graph) => {
                // debugger;
                if (graph.hasSpatialArea(key)) {
                    return observableOf(graph);
                }
                return observableFrom(graph.cacheSpatialArea$(key)).pipe(mergeMap((graph$) => {
                    return graph$.pipe(catchError((error) => {
                        console.error(`Failed to cache spatial nodes (${key}).`, error);
                        return observableEmpty();
                    }));
                }));
            }), last(), mergeMap((graph) => {
                return graph.hasNodeSequence(key) ? observableOf(graph) : graph.cacheNodeSequence$(key);
            }), tap((graph) => {
                if (!graph.getNode(key).spatialEdges.cached) {
                    graph.cacheSpatialEdges(key);
                }
            })
            // Not used at the moment, throwed a bug when playing
            // finalize((): void => {
            //     if (spatialSubscription == null) {
            //         return;
            //     }
            //     this._removeFromArray(spatialSubscription, this._spatialSubscriptions);
            // })
            )
                .subscribe(() => {
                return;
            }, (error) => {
                console.error(`Failed to cache spatial edges (${key}).`, error);
            });
            // Not used at the moment, removed to not overflow an array as the removing throws a bug (see above)
            // if (!spatialSubscription.closed) {
            //     this._spatialSubscriptions.push(spatialSubscription);
            // }
        }
        // NOTE: Obs of nodes for which assets are chached and key matches.
        return node$.pipe(first((node) => {
            return node.assetsCached;
        }));
    }
    /**
     * Cache a sequence in the graph and retrieve it.
     *
     * @param {string} sequenceKey - Sequence key.
     * @returns {Observable<Sequence>} Observable emitting a single item,
     * the sequence, when it has been retrieved and its assets are cached.
     * @throws {Error} Propagates any IO node caching errors to the caller.
     */
    cacheSequence$(sequenceKey) {
        return this._graph$.pipe(first(), mergeMap((graph) => {
            if (graph.isCachingSequence(sequenceKey) || !graph.hasSequence(sequenceKey)) {
                return graph.cacheSequence$(sequenceKey);
            }
            return observableOf(graph);
        }), map((graph) => {
            return graph.getSequence(sequenceKey);
        }));
    }
    /**
     * Cache a sequence and its nodes in the graph and retrieve the sequence.
     *
     * @description Caches a sequence and its assets are cached and
     * retrieves all nodes belonging to the sequence. The node assets
     * or edges will not be cached.
     *
     * @param {string} sequenceKey - Sequence key.
     * @param {string} referenceNodeKey - Key of node to use as reference
     * for optimized caching.
     * @returns {Observable<Sequence>} Observable emitting a single item,
     * the sequence, when it has been retrieved, its assets are cached and
     * all nodes belonging to the sequence has been retrieved.
     * @throws {Error} Propagates any IO node caching errors to the caller.
     */
    cacheSequenceNodes$(sequenceKey, referenceNodeKey) {
        return this._graph$.pipe(first(), mergeMap((graph) => {
            if (graph.isCachingSequence(sequenceKey) || !graph.hasSequence(sequenceKey)) {
                return graph.cacheSequence$(sequenceKey);
            }
            return observableOf(graph);
        }), mergeMap((graph) => {
            if (graph.isCachingSequenceNodes(sequenceKey) || !graph.hasSequenceNodes(sequenceKey)) {
                return graph.cacheSequenceNodes$(sequenceKey, referenceNodeKey);
            }
            return observableOf(graph);
        }), map((graph) => {
            return graph.getSequence(sequenceKey);
        }));
    }
    /**
     * Dispose the graph service and its children.
     */
    dispose() {
        this._graph$.pipe(first()).subscribe((graph) => {
            graph.unsubscribe();
        });
        this._subscriptions.unsubscribe();
    }
    /**
     * Set a spatial edge filter on the graph.
     *
     * @description Resets the spatial edges of all cached nodes.
     *
     * @param {FilterExpression} filter - Filter expression to be applied.
     * @return {Observable<Graph>} Observable emitting a single item,
     * the graph, when the spatial edges have been reset.
     */
    setFilter$(filter) {
        this._resetSubscriptions(this._spatialSubscriptions);
        return this._graph$.pipe(first(), tap((graph) => {
            graph.resetSpatialEdges();
            graph.setFilter(filter);
        }), map(() => {
            return undefined;
        }));
    }
    /**
     * Set the graph mode.
     *
     * @description If graph mode is set to spatial, caching
     * is performed with emphasis on spatial edges. If graph
     * mode is set to sequence no tile data is requested and
     * no spatial edges are computed.
     *
     * When setting graph mode to sequence all spatial
     * subscriptions are aborted.
     *
     * @param {GraphMode} mode - Graph mode to set.
     */
    setGraphMode(mode) {
        if (this._graphMode === mode) {
            return;
        }
        if (mode === GraphMode.Sequence) {
            this._resetSubscriptions(this._spatialSubscriptions);
        }
        this._graphMode = mode;
        this._graphModeSubject$.next(this._graphMode);
    }
    /**
     * Reset the graph.
     *
     * @description Resets the graph but keeps the nodes of the
     * supplied keys.
     *
     * @param {Array<string>} keepKeys - Keys of nodes to keep in graph.
     * @return {Observable<Node>} Observable emitting a single item,
     * the graph, when it has been reset.
     */
    reset$(keepKeys) {
        this._abortSubjects(this._firstGraphSubjects$);
        this._resetSubscriptions(this._initializeCacheSubscriptions);
        this._resetSubscriptions(this._sequenceSubscriptions);
        this._resetSubscriptions(this._spatialSubscriptions);
        return this._graph$.pipe(first(), tap((graph) => {
            graph.reset(keepKeys);
        }), map(() => {
            return undefined;
        }));
    }
    /**
     * Uncache the graph.
     *
     * @description Uncaches the graph by removing tiles, nodes and
     * sequences. Keeps the nodes of the supplied keys and the tiles
     * related to those nodes.
     *
     * @param {Array<string>} keepKeys - Keys of nodes to keep in graph.
     * @param {string} keepSequenceKey - Optional key of sequence
     * for which the belonging nodes should not be disposed or
     * removed from the graph. These nodes may still be uncached if
     * not specified in keep keys param.
     * @return {Observable<Graph>} Observable emitting a single item,
     * the graph, when the graph has been uncached.
     */
    uncache$(keepKeys, keepSequenceKey) {
        return this._graph$.pipe(first(), tap((graph) => {
            graph.uncache(keepKeys, keepSequenceKey);
        }), map(() => {
            return undefined;
        }));
    }
    _abortSubjects(subjects) {
        for (const subject of subjects.slice()) {
            this._removeFromArray(subject, subjects);
            subject.error(new Error("Cache node request was aborted."));
        }
    }
    _removeFromArray(object, objects) {
        const index = objects.indexOf(object);
        if (index !== -1) {
            objects.splice(index, 1);
        }
    }
    _resetSubscriptions(subscriptions) {
        for (const subscription of subscriptions.slice()) {
            this._removeFromArray(subscription, subscriptions);
            if (!subscription.closed) {
                subscription.unsubscribe();
            }
        }
    }
}
//# sourceMappingURL=GraphService.js.map